/**
 * API Client Configuration
 * Base Axios instance with interceptors for authentication and error handling
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { getApiUrl, isDebugMode } from '@/utils/env';
import { redactSensitiveData, redactSensitiveUrl } from '@/utils/redact';
import { getAccessToken, clearTokens, refreshAccessToken } from '@/utils/token';
import { buildLoginRedirectUrl } from '@/utils/returnTo';
import { useAuthStore } from '@/store/auth.store';

/**
 * Create the base Axios instance
 */
const resolvedBaseUrl = getApiUrl();

export const apiClient: AxiosInstance = axios.create({
  baseURL: resolvedBaseUrl,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with requests
});

/**
 * Request Interceptor
 * Adds authentication token to requests if available
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Log request in debug mode
    if (isDebugMode()) {
      console.log(
        `[API Request] ${config.method?.toUpperCase()} ${redactSensitiveUrl(config.url)}`,
        {
          params: redactSensitiveData(config.params),
          data: redactSensitiveData(config.data),
        }
      );
    }

    // Get token from token utilities
    const token = getAccessToken();

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add workspace context header
    const workspaceId = useAuthStore.getState().currentWorkspaceId;
    if (workspaceId && config.headers) {
      config.headers['X-Workspace-Id'] = String(workspaceId);
    }

    return config;
  },
  (error: AxiosError) => {
    if (isDebugMode()) {
      console.error('[API Request Error]', {
        message: error.message,
        url: redactSensitiveUrl(error.config?.url),
        status: error.response?.status,
      });
    }
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * Handles authentication errors and logging
 */
apiClient.interceptors.response.use(
  (response) => {
    // Log response in debug mode
    if (isDebugMode()) {
      console.log(
        `[API Response] ${response.config.method?.toUpperCase()} ${redactSensitiveUrl(response.config.url)}`,
        {
          status: response.status,
          data: redactSensitiveData(response.data),
        }
      );
    }

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // Log error in debug mode
    if (isDebugMode()) {
      console.error('[API Response Error]', {
        url: redactSensitiveUrl(error.config?.url),
        status: error.response?.status,
        message: error.message,
        data: redactSensitiveData(error.response?.data),
      });
    }

    // Handle 401 Unauthorized - Token expired or invalid
    // Skip token refresh for auth endpoints (login, register, etc.)
    const isAuthEndpoint = originalRequest?.url?.startsWith('/auth/');
    if (error.response?.status === 401 && originalRequest && !isAuthEndpoint) {
      // Check if we've already tried to refresh
      const hasRetried = (originalRequest as InternalAxiosRequestConfig & { _retry?: boolean })
        ._retry;

      if (!hasRetried) {
        (originalRequest as InternalAxiosRequestConfig & { _retry?: boolean })._retry = true;

        try {
          // Deduplicated refresh — concurrent 401s share a single in-flight request
          const tokens = await refreshAccessToken(async (rt) => {
            const response = await axios.post(`${getApiUrl()}/auth/refresh`, {
              refreshToken: rt,
            });
            return response.data;
          });

          // Sync Zustand store with new tokens
          useAuthStore.getState().updateTokens({
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
          });

          // Update the failed request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
          }

          // Retry the original request
          return apiClient(originalRequest);
        } catch (refreshError) {
          // Refresh failed - clear tokens and redirect to login, preserving
          // the current URL so the user can return after re-authenticating.
          clearTokens();

          if (typeof window !== 'undefined') {
            window.location.href = buildLoginRedirectUrl(window.location, 'expired');
          }

          return Promise.reject(refreshError);
        }
      }

      // If we've already retried, clear auth and redirect
      clearTokens();

      if (typeof window !== 'undefined') {
        window.location.href = buildLoginRedirectUrl(window.location, 'expired');
      }
    }

    // Handle other errors
    // This can be extended with error notification system
    return Promise.reject(error);
  }
);

/**
 * API Error class for type-safe error handling
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data: unknown,
    message?: string
  ) {
    super(message || statusText);
    this.name = 'ApiError';
  }
}

/**
 * Helper function to extract error message from API error
 */
export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    // Check for API error response
    if (error.response?.data?.message) {
      return error.response.data.message;
    }

    // Check for validation errors
    if (error.response?.data?.errors) {
      const errors = error.response.data.errors;
      if (Array.isArray(errors)) {
        return errors.map((e: { message?: string }) => e.message || String(e)).join(', ');
      }
      return String(errors);
    }

    // Fallback to status text
    return error.response?.statusText || error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unknown error occurred';
};

/**
 * Type-safe API response wrapper
 */
// TODO: align with @skillspec/shared-types ApiResponse — actual API envelope is { data, meta?: { timestamp, requestId? } }
export interface ApiResponse<T> {
  data: T;
  message?: string;
  meta?: {
    page?: number;
    limit?: number;
    total?: number;
    totalPages?: number;
  };
}

/**
 * Export the client as default
 */
export default apiClient;
